import React, { useEffect, useMemo, useState } from 'react'
import { Empty, Pagination, Spin, message } from 'antd'
import { fetchOrganizationMembers } from '../api/organization'
import type { Member, MemberListFilters, MemberPage, MemberTableGroup, Organization } from '../types'
import { buildRoleChips, memberQueryKey, orgChipsForMember, resolveOrganizationMemberCount } from '../utils'
import MemberTable from './MemberTable'
import MemberTableState from './MemberTableState'

interface OrganizationMembersProps {
  organization: Organization | undefined
  organizations: Organization[]
  search: string
  filters: MemberListFilters
  filterSelection: MemberListFilters
  onFiltersChange: (filters: MemberListFilters) => void
  refreshKey?: number
  onInvite: (organization: Organization) => void
  onAssign?: (member: Member, organization: Organization) => void
  onEdit?: (member: Member, organization: Organization) => void
  onDisable?: (member: Member, organization: Organization) => void
  onEnable?: (member: Member, organization: Organization) => void
  onResendInvite?: (member: Member, organization: Organization) => Promise<void>
  currentCustomerId?: string | null
  onUpgrade?: (organization: Organization) => void
}

const PAGE_SIZE = 10

const OrganizationMembers: React.FC<OrganizationMembersProps> = ({
  organization,
  organizations,
  search,
  filters,
  filterSelection,
  onFiltersChange,
  refreshKey = 0,
  onInvite,
  onAssign,
  onEdit,
  onDisable,
  onEnable,
  onResendInvite,
  currentCustomerId,
  onUpgrade,
}) => {
  const [page, setPage] = useState<MemberPage>({
    members: [],
    total: 0,
    page: 1,
    pageSize: PAGE_SIZE,
  })
  const [loading, setLoading] = useState(true)
  const orgScopedFilters = useMemo<MemberListFilters>(
    () => ({ ...filters, organizationIds: [] }),
    [filters],
  )
  const queryKey = memberQueryKey(search, orgScopedFilters)

  // Switching org tabs must drop the previous org's rows so the loading state is visible.
  useEffect(() => {
    setPage({
      members: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,
    })
    setLoading(true)
  }, [organization?.id])

  useEffect(() => {
    setPage((prev) => ({ ...prev, page: 1 }))
  }, [queryKey])

  useEffect(() => {
    let cancelled = false

    const loadPage = async () => {
      if (!organization) return
      setLoading(true)
      try {
        const result = await fetchOrganizationMembers({
          customerId: organization.customerId,
          page: page.page,
          pageSize: page.pageSize,
          search,
          filters: orgScopedFilters,
        })
        if (!cancelled) {
          setPage(result)
        }
      } catch (error) {
        if (!cancelled) {
          message.error(error instanceof Error ? error.message : 'Failed to load users')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPage()
    return () => {
      cancelled = true
    }
  }, [orgScopedFilters, organization?.customerId, organization?.id, page.page, page.pageSize, queryKey, refreshKey, search])

  const groups: MemberTableGroup[] = useMemo(() => {
    if (!organization) return []
    return [{
      org: organization,
      rows: page.members.map((member) => ({
        member,
        roleInOrg: member.roles[organization.id],
        roleChips: buildRoleChips(organization.id, member.roleLists?.[organization.id] || [member.roles[organization.id]]),
        orgChips: orgChipsForMember(member, organizations, organization),
      })),
    }]
  }, [organization, organizations, page.members])

  if (!organization) {
    return <Empty description="Select an organization" />
  }

  if (loading && page.members.length === 0) {
    return (
      <MemberTableState
        loading
        roleOrganizations={[organization]}
        filters={filterSelection}
        onFiltersChange={onFiltersChange}
      />
    )
  }

  return (
    <Spin spinning={loading} wrapperClassName="um-refactor__table-spin">
      <div className="um-refactor__member-table-layout">
        <MemberTable
          groups={groups}
          roleOrganizations={organization ? [organization] : []}
          memberCount={resolveOrganizationMemberCount(organization, page.total)}
          filters={filterSelection}
          onFiltersChange={onFiltersChange}
          currentCustomerId={currentCustomerId}
          onInvite={onInvite}
          onAssign={onAssign}
          onEdit={onEdit}
          onDisable={onDisable}
          onEnable={onEnable}
          onResendInvite={onResendInvite}
          onUpgrade={onUpgrade}
        />
        <div className="um-refactor__pagination">
          <Pagination
            current={page.page}
            pageSize={page.pageSize}
            total={page.total}
            showSizeChanger
            showTotal={(total) => `${total} users`}
            onChange={(nextPage, nextPageSize) => {
              setPage((prev) => ({
                ...prev,
                page: nextPageSize !== prev.pageSize ? 1 : nextPage,
                pageSize: nextPageSize,
              }))
            }}
          />
        </div>
      </div>
    </Spin>
  )
}

export default OrganizationMembers
